import { linkConditionsText, type LinkSentenceCondition } from './link-sentence';
import type { LinkTrigger } from './links';
import { t } from './i18n/t';

/**
 * Connectors: the transport shapes of the Gmail connector and the pure logic
 * both apps share — which field of a list holds a party's email, what counts
 * as an address, what may reach a mail header, and the limits every send is
 * held to. docs/plans/connectors-gmail.md, Decided 10 and 12.
 */

/** The viewer's own Gmail connection, as `GET /v1/connectors` answers it. */
export type GmailConnectionView = {
  state: 'connected' | 'broken';
  /** The Gmail address the person connected, which every email goes from. */
  address: string;
  /** ISO time the connection was made. */
  connectedAt: string;
  /** ISO time Gmail stopped answering; only on a broken connection. */
  brokenAt?: string;
};

/** `GET /v1/connectors`: the viewer's connectors in this workspace. */
export type ConnectorsStatus = {
  gmail: {
    /** False when the server has no Google client or token key: the card reads "Soon". */
    available: boolean;
    connection: GmailConnectionView | null;
    /** Emails waiting for the viewer's yes. */
    waiting: number;
    /** The viewer's rules that are on but paused. */
    pausedRules: number;
    /** All of the viewer's rules. */
    rules: number;
    /** Drafts and sends written today, in the viewer's zone day, against DAY_LIMIT. */
    usedToday: number;
  };
};

/** `POST /v1/connectors/gmail/start`: where to come back to after Google. */
export type GmailStartRequest = {
  /** `/business/connectors/gmail` or `/coach/<uuid>`; anything else comes back to the Gmail page. */
  returnTo?: string;
};

/** The Google address to send the tab to (a full-tab redirect). */
export type GmailStartResponse = { url: string };

/** `DELETE /v1/connectors/gmail`. */
export type GmailDisconnectResponse = {
  /** False when Google could not be reached to revoke; the connection is gone either way. */
  revoked: boolean;
  pausedRules: number;
  leftOut: number;
};

/** The page a Gmail sign-in comes back to unless it started in a Coach thread. */
export const GMAIL_PAGE_PATH = '/business/connectors/gmail';

const COACH_THREAD = /^\/coach\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Where a Gmail sign-in may come back to: the Gmail page or a Coach thread, else the Gmail page. */
export function gmailReturnTo(value: unknown): string {
  if (typeof value !== 'string') return GMAIL_PAGE_PATH;
  return value === GMAIL_PAGE_PATH || COACH_THREAD.test(value) ? value : GMAIL_PAGE_PATH;
}

/** How an email goes: a Gmail draft, a send after the person's yes, or a send on its own. */
export type SendMode = 'draft' | 'wait' | 'own';
export const SEND_MODES: readonly SendMode[] = ['draft', 'wait', 'own'];

/** The life of one row of the send log. */
export type SendState = 'waiting' | 'sending' | 'drafted' | 'sent' | 'failed' | 'left_out';
export const SEND_STATES: readonly SendState[] = ['waiting', 'sending', 'drafted', 'sent', 'failed', 'left_out'];

/** Why an email was left out. */
export type LeftOutReason = 'by_you' | 'no_email' | 'no_party' | 'disconnected' | 'rule_removed';

/** At most this many emails a day for each person in each workspace, drafts and sends together. */
export const DAY_LIMIT = 50;
/** At most this many emails on one card. */
export const CARD_LIMIT = 20;
/** A rule run that would write more than this many emails makes every one wait for a yes. */
export const WAIT_OVER = 20;
/** At most this many bytes of files on one email (base64 makes about 24 MB, under Gmail's 25 MB). */
export const ATTACH_MAX_BYTES = 18 * 1024 * 1024;

const EMAIL_LABEL = /\be-?mail\b/i;
const EMAIL_ADDRESS = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * The field of a list that holds a party's email: the first `text` field whose
 * label says email, else null. There is no email type; this is the whole rule.
 */
export function emailFieldOf<F extends { type: string; label: string }>(fields: readonly F[]): F | null {
  for (const field of fields) {
    if (field.type === 'text' && EMAIL_LABEL.test(field.label)) return field;
  }
  return null;
}

/** Whether a value, once trimmed, is an email address. */
export function isEmailAddress(value: unknown): value is string {
  return typeof value === 'string' && EMAIL_ADDRESS.test(value.trim());
}

/** A value safe to put in a mail header: every CR and LF removed. */
export function headerSafe(value: string): string {
  return value.replace(/[\r\n]+/g, '');
}

/** The address a value holds, trimmed, or null when it holds none. */
export function emailOf(value: unknown): string | null {
  return isEmailAddress(value) ? value.trim() : null;
}

const escaped = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** What may come right before a typed address: the start, a space, or < ( [ , ; : " > and the curly quotes “ ‘. */
const ADDRESS_BOUNDARY = `(?:^|[\\s<(\\[,;:"“‘>])`;
/** Marks that may wrap a typed address after a boundary: a quote, or markdown's ** __ * _ and `. */
const ADDRESS_WRAP = "(?:'|\\*{1,2}|_{1,2}|`)?";

/**
 * Whether a person typed this address in some text (Decided 9): the whole
 * address, any case, not the tail of a longer one ("bob@x.com" is not in
 * "jimbob@x.com") nor the head of one ("a@b.co" is not in "a@b.com"). A full
 * stop after it ends a sentence, so "write to a@b.com." holds "a@b.com".
 *
 * Before it there must be a real boundary: the start, a space, or one of
 * < ( [ , ; : " > “ ‘ — or a ' or markdown's ** __ * _ ` that itself
 * follows one of those, so a quoted 'a@b.com', “a@b.com” and **a@b.com**
 * count, and "o'bob@x.com" does not hold "bob@x.com". Any other
 * character a local part may hold (' & * / = ? ^ { | } ~ ! # $ and the rest)
 * makes it the tail of a longer address.
 */
export function addressInText(address: string, text: string): boolean {
  const wanted = address.trim();
  if (!isEmailAddress(wanted)) return false;
  return new RegExp(`(?<=${ADDRESS_BOUNDARY}${ADDRESS_WRAP})${escaped(wanted)}(?![a-z0-9-]|\\.\\w)`, 'i').test(text);
}

/** A busy Gmail (429) is tried again this many minutes after the 1st, 2nd and 3rd try; after the 4th the email fails. */
export const BUSY_RETRY_MINUTES: readonly number[] = [1, 2, 4];

/**
 * Minutes until the next try after `attempts` tries that found Gmail busy,
 * or null when the email has had every retry and fails.
 */
export function busyRetryMinutes(attempts: number): number | null {
  if (!Number.isInteger(attempts) || attempts < 1) return BUSY_RETRY_MINUTES[0]!;
  return BUSY_RETRY_MINUTES[attempts - 1] ?? null;
}

/** How many more emails the day limit admits after `used` were written today. */
export function sendsLeft(used: number): number {
  return Math.max(0, DAY_LIMIT - Math.max(0, used));
}

/** Whether `asked` more emails fit under the day limit after `used`: all of them, or none (a card is refused whole). */
export function fitsDayLimit(used: number, asked: number): boolean {
  return asked <= sendsLeft(used);
}

/* ------------------------------------------------------------------ the sends */

/** One email of a card, as `POST /v1/connectors/gmail/sends` takes it: a party or a typed address. */
export type EmailMessage = {
  /** A row of a list; its address is read from the list's email field. */
  partyEntryId?: string | null;
  /** An address the person typed in the chat; used only when no party is named. */
  to?: string | null;
  subject: string;
  body: string;
  /** The entries it is about. */
  entryIds?: string[];
  /** An attachment field of the first entry whose files go with it. */
  attachFieldId?: string | null;
  /**
   * Its place on the card, under CARD_LIMIT and once per request; its place
   * in the request when left out. A repeated send is answered email by email
   * at this place, so leaving one out between tries never moves another.
   */
  cardIndex?: number | null;
};

/** `POST /v1/connectors/gmail/sends`: one card's emails, at most CARD_LIMIT. */
export type GmailSendRequest = {
  /** The chat the card came from; a typed address must be in one of its messages. */
  threadId: string;
  /** The card; with each email's index it makes a repeated send answer the first result. */
  cardId: string;
  /** Save as drafts, or Send. */
  mode: 'draft' | 'send';
  messages: EmailMessage[];
};

/** What came of one email. A party with no address is `left_out` with `no_email` and no log row. */
export type GmailSendResult = {
  /** Its place on the card (the email's `cardIndex`, else its position in the request), or null for an approval. */
  index: number | null;
  /** The log row, or null when none was written. */
  id: string | null;
  to: string | null;
  /** True when the address was typed by the person. */
  typed: boolean;
  state: SendState;
  reason: string | null;
  gmailDraftId: string | null;
};

export type GmailSendResponse = { sends: GmailSendResult[] };

/** `POST /v1/connectors/gmail/sends/approve` and `/leave`: waiting emails, at most CARD_LIMIT. */
export type SendIdsRequest = { ids: string[] };
export type GmailLeaveResponse = { leftOut: number };

/** One row of the owner's own log, `GET /v1/connectors/gmail/sends`. */
export type SendLogItem = {
  id: string;
  origin: 'ask' | 'rule';
  ruleId: string | null;
  ruleName: string | null;
  partyEntryId: string | null;
  /** The list the party is a row of, so the log can open the party's entry (Add an email). */
  partyRecordId: string | null;
  /** The party's name as its row is titled now, for the log's To; null for a typed address or a row gone or closed to the reader. */
  partyName: string | null;
  entryIds: string[];
  from: string | null;
  to: string | null;
  subject: string;
  body: string;
  attachmentNames: string[];
  state: SendState;
  reason: string | null;
  attempts: number;
  createdAt: string;
  sentAt: string | null;
};

export type SendLogResponse = { sends: SendLogItem[]; nextCursor: string | null };

/** One email written to a party, as any member who may see the party reads it: never the body. */
export type PartySend = {
  id: string;
  createdAt: string;
  userId: string;
  /** True when the reader wrote it. */
  byYou: boolean;
  /** Null when the list's email field is hidden from the reader. */
  to: string | null;
  /** Null when a rule's subject names a field of the entry and the reader did not write it. */
  subject: string | null;
  state: SendState;
};

/** `GET /v1/connectors/sends?partyEntryId=`. */
export type PartySendsResponse = { sends: PartySend[] };

/* ------------------------------------------------------------------ the rules */

/** 1 is Monday, 7 is Sunday (ISO). */
export type RuleWeekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;
/** A day of the month a schedule may run on: 1 to 28, so every month has it. */
export type RuleMonthDay =
  | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14
  | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22 | 23 | 24 | 25 | 26 | 27 | 28;
/** The last day of the month a monthly schedule may name. */
export const RULE_MONTH_DAY_MAX = 28;

/** When a schedule runs: every day, every week on a weekday, or every month on a day; `time` is `HH:MM`, 24-hour. */
export type RuleSchedule =
  | { readonly every: 'day'; readonly time: string }
  | { readonly every: 'week'; readonly weekday: RuleWeekday; readonly time: string }
  | { readonly every: 'month'; readonly day: RuleMonthDay; readonly time: string };

/**
 * When a rule fires (Decided 13). `saved`: an entry of the rule's record is
 * created or changed so the condition goes from not holding to holding.
 * `schedule`: at each slot, in `zone`, over the entries the condition holds for.
 */
export type RuleWhen =
  | { readonly kind: 'saved'; readonly condition: LinkTrigger }
  | { readonly kind: 'schedule'; readonly schedule: RuleSchedule; readonly zone: string; readonly condition: LinkTrigger };

/** Who an email of a rule goes to: the list a reference field points at, or the rule's own list. */
export type RuleTo = { readonly kind: 'via'; readonly fieldId: string } | { readonly kind: 'self' };

/** Why a rule that is on does not run. */
export type RulePausedReason = 'broken' | 'disconnected' | 'no_access' | 'no_email_field' | 'field_gone';
export const RULE_PAUSED_REASONS: readonly RulePausedReason[] = ['broken', 'disconnected', 'no_access', 'no_email_field', 'field_gone'];

/** A rule as it is made: When, To, the Email, Attach and Then. */
export type RuleSpec = {
  readonly name: string;
  readonly recordId: string;
  readonly when: RuleWhen;
  readonly to: RuleTo;
  readonly subject: string;
  readonly body: string;
  /** The fields named on each line of `{{items}}`. */
  readonly itemFields: readonly string[];
  /** An attachment field of the record whose files go with each email. */
  readonly attachFieldId: string | null;
  /** Then: null until the person picks one. */
  readonly mode: SendMode | null;
};

/** One rule of the viewer's, as `GET /v1/connectors/gmail/rules` answers it. */
export type ConnectorRuleView = RuleSpec & {
  readonly id: string;
  readonly on: boolean;
  readonly pausedReason: RulePausedReason | null;
  /** The address it sends from, set when it was turned on. */
  readonly fromAddress: string | null;
  readonly lastRunAt: string | null;
  readonly nextRunAt: string | null;
  /** Its emails waiting for the viewer's yes. */
  readonly waiting: number;
  readonly createdAt: string;
};

/** `POST /v1/connectors/gmail/rules`: a new rule, or with `replaces` a new version of one. */
export type RuleCreateRequest = RuleSpec & { readonly on?: boolean; readonly replaces?: string | null };
/** `PATCH /v1/connectors/gmail/rules/:id`. */
export type RulePatchRequest = { readonly on?: boolean; readonly mode?: SendMode; readonly name?: string };
export type RuleWriteResponse = { readonly rule: ConnectorRuleView };
export type RuleListResponse = { readonly rules: readonly ConnectorRuleView[] };
export type RuleDeleteResponse = { readonly leftOut: number };

/** Why a rule cannot be made or turned on, as `validateRule` and the routes say it. */
export type RuleProblemCode =
  | 'record_unknown'
  | 'condition_bad'
  | 'field_unknown'
  | 'computed_field'
  | 'hidden_filter'
  | 'via_not_reference'
  | 'no_email_field'
  | 'attach_not_attachment'
  | 'schedule_bad'
  | 'template_bad'
  | 'mode_missing'
  | 'typed_address';

/* -------------------------------------------------------------- the template */

/** The placeholders of a rule's email (Decided 13). */
export const RULE_PLACEHOLDERS = ['{{party}}', '{{field:<fieldId>}}', '{{items}}'] as const;

const PLACEHOLDER = /\{\{\s*(party|items|field:([0-9a-zA-Z_-]+))\s*\}\}/g;

/** The field ids a template's `{{field:<id>}}` placeholders name, each once. */
export function templateFieldIds(template: string): string[] {
  const out = new Set<string>();
  for (const match of template.matchAll(PLACEHOLDER)) if (match[2] !== undefined) out.add(match[2]);
  return [...out];
}

/** Whether a template names any value of the entry, so a reader who did not write it may not read it. */
export function templateNamesFields(template: string): boolean {
  return templateFieldIds(template).length > 0;
}

/**
 * A rule's subject or body, written for one party: `{{party}}` is the party's
 * title, `{{field:<id>}}` a value of the entry as the export writes it (empty
 * when it has none), `{{items}}` one line per entry. Anything else stays as written.
 */
export function renderTemplate(
  template: string,
  input: { readonly party: string; readonly values: Readonly<Record<string, string>>; readonly items: readonly string[] },
): string {
  return template.replace(PLACEHOLDER, (_whole, name: string, fieldId: string | undefined) => {
    if (name === 'party') return input.party;
    if (name === 'items') return input.items.join('\n');
    return input.values[fieldId ?? ''] ?? '';
  });
}

/* -------------------------------------------------------------- the schedule */

const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Whether a schedule is one a rule may keep. */
export function isRuleSchedule(value: unknown): value is RuleSchedule {
  if (typeof value !== 'object' || value === null) return false;
  const held = value as Record<string, unknown>;
  if (typeof held.time !== 'string' || !TIME.test(held.time)) return false;
  if (held.every === 'day') return true;
  if (held.every === 'week') return Number.isInteger(held.weekday) && (held.weekday as number) >= 1 && (held.weekday as number) <= 7;
  if (held.every === 'month') return Number.isInteger(held.day) && (held.day as number) >= 1 && (held.day as number) <= RULE_MONTH_DAY_MAX;
  return false;
}

/** Whether this runtime knows a zone by that name. */
export function knownZone(zone: unknown): zone is string {
  if (typeof zone !== 'string' || zone.trim() === '') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** How far a zone's wall clock is ahead of UTC at one instant, in milliseconds. */
const offsetAt = (zone: string, at: number): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(at));
  const part = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((one) => one.type === type)?.value ?? 0);
  const wall = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'));
  return wall - (at - (at % 1000));
};

/**
 * The instant a wall clock (as a UTC reading) names in a zone; read twice, as
 * an offset may move between. A reading the clocks skip (02:30 when 02:00
 * jumps to 03:00, or a midnight that jumps to 01:00) is taken with the offset
 * before the change, which names the instant just after it: 03:30, or 01:00,
 * so it never lands on the day before.
 */
const wallToInstant = (wall: number, zone: string): number => {
  const first = wall - offsetAt(zone, wall);
  const instant = wall - offsetAt(zone, first);
  if (instant + offsetAt(zone, instant) === wall) return instant;
  // Skipped: the offsets a day either side are the ones before and after the change.
  const DAY_MS = 86_400_000;
  const before = offsetAt(zone, wall - DAY_MS);
  const after = offsetAt(zone, wall + DAY_MS);
  return wall - Math.min(before, after);
};

/**
 * The first slot of a schedule strictly after `after`, in `zone` (an unknown
 * zone is UTC). A time the clocks skip that day comes out at the instant the
 * same reading names after the change.
 */
export function nextRuleSlot(schedule: RuleSchedule, zone: string, after: Date): Date {
  const named = knownZone(zone) ? zone : 'UTC';
  const [hours, minutes] = schedule.time.split(':').map(Number) as [number, number];
  const now = after.getTime();
  // The wall-clock day `after` falls on there, as a UTC midnight.
  const local = now + offsetAt(named, now);
  const today = Date.UTC(new Date(local).getUTCFullYear(), new Date(local).getUTCMonth(), new Date(local).getUTCDate());
  const DAY_MS = 86_400_000;
  // Two months of days always holds the next slot of any schedule.
  for (let ahead = 0; ahead <= 62; ahead += 1) {
    const day = new Date(today + ahead * DAY_MS);
    if (schedule.every === 'week' && ((day.getUTCDay() + 6) % 7) + 1 !== schedule.weekday) continue;
    if (schedule.every === 'month' && day.getUTCDate() !== schedule.day) continue;
    const slot = wallToInstant(day.getTime() + hours * 3_600_000 + minutes * 60_000, named);
    if (slot > now) return new Date(slot);
  }
  return new Date(now + DAY_MS);
}

/**
 * The first instant of the zone day after the one `at` falls on: when a party
 * a rule already wrote to today may be written to again.
 */
export function nextZoneDayStart(zone: string, at: Date): Date {
  return nextRuleSlot({ every: 'day', time: '00:00' }, zone, at);
}

/* ---------------------------------------------------------------- the words */

/** A clock reading as a person reads it: 9:00 am. */
const clockWords = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number) as [number, number];
  return new Date(Date.UTC(2024, 0, 1, hours, minutes))
    .toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' })
    .toLowerCase();
};

/** A weekday's name: Monday. 1 January 2024 was a Monday. */
const weekdayWords = (weekday: RuleWeekday): string =>
  new Date(Date.UTC(2024, 0, weekday)).toLocaleDateString('en-IN', { weekday: 'long', timeZone: 'UTC' });

/** What `ruleWhenWords` needs besides the rule: the record's name, its condition in words, the viewer's zone. */
export type RuleWhenWords = {
  /** The record's name. */
  readonly record: string;
  /** The condition, each value already in words, as the Links sentence takes it. */
  readonly groups: readonly (readonly LinkSentenceCondition[])[];
  /** The viewer's own zone; a schedule in another one names its zone. */
  readonly viewerZone: string | null;
};

/**
 * When a rule fires, in words: "Every Monday, 9:00 am", "The 1st of each
 * month, 10:00 am (Asia/Kolkata)", or for a saved rule the Links sentence's
 * own "When a Items entry is saved with Stock at most 0".
 */
export function ruleWhenWords(when: RuleWhen, words: RuleWhenWords): string {
  if (when.kind === 'saved') {
    const conditions = linkConditionsText(words.groups);
    const an = /^[aeiou]/i.test(words.record.trim());
    if (conditions === '') return t(an ? 'link.sentence.when_an' : 'link.sentence.when_a', { record: words.record });
    return t(an ? 'link.sentence.when_an_with' : 'link.sentence.when_a_with', { record: words.record, conditions });
  }
  const schedule = when.schedule;
  const time = clockWords(schedule.time);
  const said =
    schedule.every === 'day'
      ? t('connector.when.daily', { time })
      : schedule.every === 'week'
        ? t('connector.when.weekly', { weekday: weekdayWords(schedule.weekday), time })
        : t(`connector.when.monthly.${schedule.day}`, { time });
  return words.viewerZone === when.zone ? said : t('connector.when.in_zone', { when: said, zone: when.zone });
}
